import { useCallback } from 'react';

import { useBlobPreview, type PreviewKind } from '../../../hooks/useBlobPreview';
import type { ApiError } from '../../../lib/apiError';
import { repositoryApi } from './repositoryApi';

export { previewKindFor, type PreviewKind } from '../../../hooks/useBlobPreview';

/**
 * A document's stored file — or one of its archived versions — as a URL the browser can render.
 *
 * **The security-bearing half lives in `useBlobPreview`** — the verified type, the inert
 * allow-list, the re-typed blob and the object-URL lifecycle — shared with every other preview in
 * the product. This supplies only WHICH bytes: the document's current file, or a version's.
 *
 * A second id rather than a fetcher callback for the version, deliberately: two strings cannot be
 * got wrong, and the KEY the shared hook re-fetches on is derived from both.
 */
export function useDocumentPreview(
  id: string | undefined,
  contentType: string | null | undefined,
  versionId?: string,
): {
  kind: PreviewKind;
  url: string | null;
  loading: boolean;
  error: ApiError | null;
} {
  // `id!`: the shared hook calls this only while its key is set, and the key needs an id.
  const load = useCallback(
    (signal: AbortSignal) =>
      versionId ? repositoryApi.downloadVersion(id!, versionId, signal) : repositoryApi.download(id!, signal),
    [id, versionId],
  );

  return useBlobPreview(id ? `${id}:${versionId ?? 'current'}` : undefined, contentType, load);
}
